/**
 * Gym Shorts v3 — Mix & Match add-on behavior.
 * Fork of gym-shorts-v2-addon.js. Uses <gs-v3-mix> custom element
 * and gs-v3-* data attributes so it coexists with v2 without conflict.
 */
(async () => {
  const { Core } = await importModule('Core');
  const { publishEvent } = await importModule('Utils');

  // Global handler for "+N MORE" tiles — toggles overflow grid visibility
  // and hides the MORE tile itself. Works for both Choose Design and Mix & Match.
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-gs-v3-toggle-expand]');
    if (!btn) return;
    e.preventDefault();
    // Find the sibling overflow grid (next [data-gs-v3-overflow] in same parent section/panel)
    const container = btn.closest('section, [data-gs-v3-panel], .gs-v3-mix');
    if (!container) return;
    const overflow = container.querySelector('[data-gs-v3-overflow]');
    if (overflow) {
      const isHidden = overflow.classList.contains('gs-v3-grid--hidden');
      overflow.classList.toggle('gs-v3-grid--hidden');
      // Toggle MORE tile text between "+N MORE" and "SHOW LESS"
      const moreTile = btn.closest('.gs-v3-grid__item--more');
      if (moreTile) {
        const textEl = btn.querySelector('.gs-v3-more-tile__text');
        if (textEl) {
          if (isHidden) {
            // Expanding — save original text, show "LESS"
            if (!btn._originalText) btn._originalText = textEl.innerHTML;
            textEl.innerHTML = 'SHOW<br>LESS';
          } else {
            // Collapsing — restore original
            if (btn._originalText) textEl.innerHTML = btn._originalText;
          }
        }
      }
    }
  });

  customElements.define('gs-v3-mix', class extends Core {
    subscriptions = {
      'variant:update': '_onMainVariantUpdate',
      'cart:update': '_onCartSettled',
      'cart:error': '_onCartSettled'
    };

    render() {
      this._root = this.hasAttribute('data-gs-v3-mix') ? this : (this.querySelector('[data-gs-v3-mix]') || this);
      this._mainFormId = this._root.dataset.mainFormId;
      this._mainVariants = this._parseJSON(this._root.dataset.mainVariants, []);
      this._summaryEl = this.querySelector('[data-gs-v3-mix-summary]');
      this._mainForm = document.getElementById(this._mainFormId);

      this.subscribe('variant:update', { global: true });
      this.subscribe('cart:update', { global: true });
      this.subscribe('cart:error', { global: true });
      this._bindTabs();
      this._bindCards();
      this._bindFormIntercept();

      this._syncAddonsFromMain();
    }

    _onMainVariantUpdate() {
      this._syncAddonsFromMain();
    }

    // ------ Tab switching ------
    _bindTabs() {
      const tabs = this.querySelectorAll('[data-gs-v3-tab]');
      const panels = this.querySelectorAll('[data-gs-v3-panel]');
      if (tabs.length < 2) return;

      tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          const idx = tab.dataset.gsV3Tab;
          // Update tab active state
          tabs.forEach(t => {
            t.classList.remove('gs-v3-tabs__tab--active');
            t.setAttribute('aria-selected', 'false');
          });
          tab.classList.add('gs-v3-tabs__tab--active');
          tab.setAttribute('aria-selected', 'true');
          // Show/hide panels
          panels.forEach(p => {
            p.classList.toggle('gs-v3-tab-panel--hidden', p.dataset.gsV3Panel !== idx);
          });
        });
      });
    }

    _onCartSettled() {
      if (this._activeBtn) {
        this._activeBtn.classList.remove('!loading');
        this._activeBtn = null;
      }
    }

    _getMainVariantId() {
      // [name="id"] input is OUTSIDE the <form> tag and linked via form="..." attr.
      // form.elements.namedItem respects this; querySelector does not.
      let input = null;
      if (this._mainForm && this._mainForm.elements) {
        input = this._mainForm.elements.namedItem('id');
      }
      if (!input) {
        input = document.querySelector(
          `[data-element="id-input"][form="${this._mainFormId}"]`
        );
      }
      return input ? parseInt(input.value, 10) || null : null;
    }

    // Read line-item properties (properties[X]) attached to main form.
    // Required because we preventDefault() the native form submit; without this,
    // any properties[] inputs (e.g. cgb_custom_text) would never reach
    // /cart/add.js → order.line_items[i].properties stays empty.
    _collectMainProperties() {
      const props = {};
      const inputs = document.querySelectorAll(
        `[name^="properties["][form="${this._mainFormId}"]`
      );
      inputs.forEach(el => {
        if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
        const m = el.name.match(/^properties\[(.+)\]$/);
        if (!m) return;
        const value = el.value;
        if (value === '' || value == null) return;
        props[m[1]] = value;
      });
      return props;
    }

    _getMainVariantSize() {
      const currentId = this._getMainVariantId();
      if (!currentId) return null;
      const match = this._mainVariants.find(v => v.id === currentId);
      return match ? match.size : null;
    }

    _syncAddonsFromMain() {
      const mainSize = this._getMainVariantSize();
      const cards = this.querySelectorAll('[data-gs-v3-addon-card]');
      cards.forEach(card => this._applyMainSizeToCard(card, mainSize));
      this._updateSummary();
    }

    _applyMainSizeToCard(card, mainSize) {
      const pills = Array.from(card.querySelectorAll('[data-gs-v3-addon-size]'));
      if (pills.length === 0) return;
      if (card.dataset.expanded !== 'true') return;

      pills.forEach(p => p.setAttribute('aria-pressed', 'false'));

      const normalize = s => String(s || '').trim().toLowerCase();
      const matched = mainSize
        ? pills.find(p => !p.disabled && normalize(p.dataset.sizeLabel) === normalize(mainSize))
        : null;
      const target = matched || pills.find(p => !p.disabled);

      if (target) {
        target.setAttribute('aria-pressed', 'true');
        card.dataset.selected = 'true';
      } else {
        card.dataset.selected = 'false';
      }
    }

    _bindCards() {
      const cards = Array.from(this.querySelectorAll('[data-gs-v3-addon-card]'));
      cards.forEach(card => {
        const toggle = card.querySelector('[data-gs-v3-addon-toggle]');
        const pills = Array.from(card.querySelectorAll('[data-gs-v3-addon-size]'));

        if (toggle) {
          toggle.addEventListener('click', e => {
            e.preventDefault();
            this._onCardToggle(card);
          });
        }

        pills.forEach(pill => {
          pill.addEventListener('click', e => {
            e.preventDefault();
            e.stopPropagation();
            if (pill.disabled) return;
            this._onPillClick(card, pill, pills);
          });
        });
      });
    }

    _onCardToggle(card) {
      const wasExpanded = card.dataset.expanded === 'true';
      const next = !wasExpanded;
      card.dataset.expanded = String(next);

      const toggle = card.querySelector('[data-gs-v3-addon-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', String(next));

      if (next) {
        const mainSize = this._getMainVariantSize();
        this._applyMainSizeToCard(card, mainSize);
      } else {
        card.querySelectorAll('[data-gs-v3-addon-size]').forEach(p =>
          p.setAttribute('aria-pressed', 'false')
        );
        card.dataset.selected = 'false';
      }
      this._updateSummary();
    }

    _onPillClick(card, pill, pills) {
      pills.forEach(p => p.setAttribute('aria-pressed', 'false'));
      pill.setAttribute('aria-pressed', 'true');
      card.dataset.selected = 'true';
      card.dataset.expanded = 'true';
      this._updateSummary();
    }

    _collectSelectedItems() {
      const pills = this.querySelectorAll(
        '[data-gs-v3-addon-card][data-expanded="true"] [data-gs-v3-addon-size][aria-pressed="true"]'
      );
      const items = [];
      const seen = new Set();
      pills.forEach(pill => {
        const variantId = parseInt(pill.dataset.variantId, 10);
        if (!variantId || seen.has(variantId)) return;
        seen.add(variantId);
        items.push({ id: variantId, quantity: 1 });
      });
      return items;
    }

    _updateSummary() {
      if (!this._summaryEl) return;
      const count = this._collectSelectedItems().length;
      if (count > 0) {
        this._summaryEl.textContent = `${count} extra pair${count > 1 ? 's' : ''} stacked — 30% off applied at checkout.`;
        this._summaryEl.dataset.active = 'true';
      } else {
        this._summaryEl.textContent = '';
        this._summaryEl.dataset.active = 'false';
      }
    }

    // Aggregate selected pills across the WHOLE page (not just this instance).
    // Needed when there are 2+ <gs-v3-mix> blocks and no <cgb-addon-pair> to
    // coordinate (e.g. custom-gym-product-* templates). Without this, a sibling
    // instance with empty selection can swallow the click flow before the one
    // with the selection runs, leaving the addon out of the cart payload.
    _collectAllAddonItems() {
      const pills = document.querySelectorAll(
        '[data-gs-v3-addon-card][data-expanded="true"] [data-gs-v3-addon-size][aria-pressed="true"]'
      );
      const items = [];
      const seen = new Set();
      pills.forEach(pill => {
        const variantId = parseInt(pill.dataset.variantId, 10);
        if (!variantId || seen.has(variantId)) return;
        seen.add(variantId);
        items.push({ id: variantId, quantity: 1 });
      });
      return items;
    }

    _bindFormIntercept() {
      this._clickHandler = e => {
        const btn = e.target.closest('[data-to-cart]');
        if (!btn) return;
        if (btn.dataset.cgbPairHandled === 'true') return;  // already claimed by a sibling intercept

        const addonItems = this._collectAllAddonItems();
        if (addonItems.length === 0) return;

        const mainId = this._getMainVariantId();
        if (!mainId) return;

        const qtyInput = document.querySelector(
          `[name="quantity"][form="${this._mainFormId}"]`
        ) || document.querySelector(`#${CSS.escape(this._mainFormId)} [name="quantity"]`);
        const mainQty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();

        btn.dataset.cgbPairHandled = 'true';
        setTimeout(() => { delete btn.dataset.cgbPairHandled; }, 100);

        btn.classList.add('!loading');
        this._activeBtn = btn;

        const mainItem = { id: mainId, quantity: mainQty };
        const mainProps = this._collectMainProperties();
        if (Object.keys(mainProps).length > 0) mainItem.properties = mainProps;
        const items = [mainItem, ...addonItems];
        publishEvent('cart:add', { items }, null);
      };
      document.addEventListener('click', this._clickHandler, true);
    }

    disconnect() {
      if (this._clickHandler) {
        document.removeEventListener('click', this._clickHandler, true);
      }
    }

    _parseJSON(str, fallback) {
      if (!str) return fallback;
      try { return JSON.parse(str); } catch (e) { return fallback; }
    }
  });
})();
